import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  IconButton,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Breadcrumbs,
  Link,
  Divider,
  Toolbar,
  AppBar,
  Drawer,
  List,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  InputAdornment,
  LinearProgress,
  Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import MyDriveIcon from '@mui/icons-material/Folder';
import PeopleIcon from '@mui/icons-material/People';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import StorageIcon from '@mui/icons-material/Storage';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import HomeIcon from '@mui/icons-material/Home';
import { useGraphQL } from '../../apollo/useGraphQL';
import {
  getFolderContents,
  getTrashcan,
  getSharingContents,
  getRecentlyDocs,
  getStorageCapacity,
  getTotalUsage,
  uploadFiles,
  downloadFile,
  downloadShare,
  softDeleteFile,
  softDeleteFolder,
  renameFile,
  renameFolder,
  generateFolder,
  restoreFile,
  restoreFolder,
  restoreSelected,
  trashSelected,
  realDeleteFile,
  realDeleteSingleFolder,
  emptyTrashProcess,
  createShare,
  renameShare,
  copyShareFileToMyDrive,
  deleteShare,
  setPermission,
  getDriveBookMark,
  generateBookMark,
  deleteDriveBookMark, copyFile, copySelected,
} from '../../api/driveApi';
import Message from '../../utils/message/Message';
import UploadNotificationPopup from './UploadNotificationPopup';
import PathDisplay from './PathDisplay';
import FileDetailsPanel from './FileDetailsPanel';
import AdminUploadDialog from './AdminUploadDialog';
import ContextMenu from './ContextMenu';
import MoveDialog from './MoveDialog';
import FileListView from './FileListView';
import DriveDashboard from './DriveDashboard';
import { useContextMenu } from '../../hooks/useContextMenu';

const DRAWER_WIDTH = 224;

const DriveManagement = () => {
  const { executeQuery, executeMutation, loading } = useGraphQL();
  const { contextMenu, openContextMenu, closeContextMenu } = useContextMenu();

  // 컨텍스트 메뉴 외부 클릭 감지
  useEffect(() => {
    if (!contextMenu.isOpen) return;

    const handleOutsideClick = (event) => {
      const contextMenuElement = event.target.closest('[data-context-menu]');
      if (!contextMenuElement) {
        closeContextMenu('outside-click');
      }
    };

    // 약간의 딜레이 후에 이벤트 리스너 등록 (우클릭 이벤트와 분리)
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleOutsideClick);
    }, 150);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [contextMenu.isOpen, closeContextMenu]);

  // ESC 키로 컨텍스트 메뉴 닫기
  useEffect(() => {
    if (!contextMenu.isOpen) return;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeContextMenu('escape-key');
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [contextMenu.isOpen, closeContextMenu]);

  // UI State
  const [view, setView] = useState('list'); // 'list' or 'grid'
  const [currentFolder, setCurrentFolder] = useState(null);
  const [breadcrumbs, setBreadcrumbs] = useState([{ name: '홈', uuid: null }]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null); // 현재 선택된 단일 아이템
  const [showTrash, setShowTrash] = useState(false);
  const [showShared, setShowShared] = useState(false);
  const [showRecent, setShowRecent] = useState(false);
  const [showBookmarks, setShowBookmarks] = useState(false); // 중요 문서함 상태 추가
  const [showStorage, setShowStorage] = useState(false); // 저장용량 상태 추가
  const [showHome, setShowHome] = useState(true); // 홈 대시보드 상태 추가 - 기본값을 true로 설정
  const [totalUsage, setTotalUsage] = useState({ maxUsage: 0, used: 0 }); // 전체 사용량 상태 추가
  const [storageAnimationProgress, setStorageAnimationProgress] = useState(0); // 저장용량 메인 게이지 애니메이션

  // 정렬 관련 state
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');

  // Data State
  const [items, setItems] = useState([]);

  // Dialog States
  const [renameDialog, setRenameDialog] = useState(false);
  const [newFolderDialog, setNewFolderDialog] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [newFolderName, setNewFolderName] = useState('');
  const [renamingItem, setRenamingItem] = useState(null);

  // 파일 업로드 상태 관리
  const [uploadNotifications, setUploadNotifications] = useState([]);
  const [adminUploadDialog, setAdminUploadDialog] = useState(false);
  const [moveDialog, setMoveDialog] = useState({ open: false, item: null });
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState({ open: false, item: null });

  // 저장용량 상태 관리
  const [storageData, setStorageData] = useState({
    files: [],
    meta: [],
    maxStorageGb: 0
  });



  // 파일명 표시 함수 (파일인 경우 확장자 포함)
  const getDisplayName = (item) => {
    if (!item.extension) {
      // 폴더인 경우 이름만 표시
      return item.name;
    } else {
      // 파일인 경우 이름.확장자 형태로 표시
      return `${item.name}.${item.extension}`;
    }
  };

  // 파일명 추출 및 인코딩 처리 함수
  const getDownloadFileName = (response, fallbackName) => {
    let fileName = fallbackName;
    const contentDisposition = response.headers.get('Content-Disposition');
    
    if (contentDisposition) {
      try {
        // RFC 5987 형태: filename*=UTF-8''encoded_filename
        const utf8Match = contentDisposition.match(/filename\*=UTF-8''(.+)/);
        if (utf8Match) {
          fileName = decodeURIComponent(utf8Match[1]);
        } else {
          // 일반 형태: filename="filename" 
          const normalMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
          if (normalMatch) {
            fileName = normalMatch[1].replace(/['"]/g, '');
            // ISO-8859-1 디코딩 후 UTF-8로 재인코딩
            try {
              fileName = decodeURIComponent(escape(fileName));
            } catch (e) {
              // 인코딩 변환 실패시 원본 사용
            }
          }
        }
      } catch (error) {
        console.warn('파일명 파싱 중 오류:', error);
      }
    }
    
    return fileName;
  };

  const loadCurrentFolderContents = useCallback(async () => {
    try {
      let data;
      if (showTrash && !currentFolder) {
        // 휴지통 루트만 getTrashcan 호출
        data = await getTrashcan(executeQuery);
      } else if (showShared && !currentFolder) {
        // 공유 문서함 루트 - getSharingContents 호출
        data = await getSharingContents(executeQuery, null);
      } else if (showShared && currentFolder) {
        // 공유 문서함 내 폴더
        data = await getSharingContents(executeQuery, currentFolder);
      } else if (showBookmarks && !currentFolder) {
        // 중요 문서함 루트 - getDriveBookMark 호출
        data = await getDriveBookMark(executeQuery);
      } else if (showStorage && !currentFolder) {
        // 저장용량 루트 - getStorageCapacity 호출
        const storageResult = await getStorageCapacity(executeQuery);
        setStorageData(storageResult);
        data = storageResult.files || [];
      } else {
        // 일반 폴더 또는 휴지통 내 폴더는 getFolderContents 호출
        // 내 드라이브(showTrash=false, showShared=false, showRecent=false, showBookmarks=false)인 경우 shared: false
        // 최근 문서함에서 폴더 진입 시 shared: true
        let shared = null;
        if (!showTrash && !showShared && !showRecent && !showBookmarks) {
          shared = false; // 내 드라이브
        } else if (showRecent) {
          shared = true; // 최근 문서함
        }
        data = await getFolderContents(executeQuery, currentFolder, shared);
      }
      setItems(data || []);
    } catch (error) {
      Message.showError({ message: '데이터를 불러오는 중 오류가 발생했습니다.' });
    }
  }, [executeQuery, currentFolder, showTrash, showShared, showBookmarks, showStorage]);

  // 즐겨찾기 데이터 로드 - 현재는 bookmarkId로 관리하므로 별도 로드 불필요
  const loadBookmarks = useCallback(async () => {
    // bookmarkId는 각 item 데이터에 포함되어 전달되므로 별도 처리 불필요
  }, []);


  // Load initial data
  useEffect(() => {
    const loadData = async () => {
      try {
        let data;
        if (showTrash && !currentFolder) {
          // 휴지통 루트만 getTrashcan 호출
          data = await getTrashcan(executeQuery);
        } else if (showRecent && !currentFolder) {
          // 최근 문서함 루트 - getRecentlyDocs 호출
          data = await getRecentlyDocs(executeQuery);
        } else if (showShared && !currentFolder) {
          // 공유 문서함 루트 - getSharingContents 호출
          data = await getSharingContents(executeQuery, null);
        } else if (showBookmarks && !currentFolder) {
          // 중요 문서함 루트 - getDriveBookMark 호출
          data = await getDriveBookMark(executeQuery);
        } else if (showStorage && !currentFolder) {
          // 저장용량 루트 - getStorageCapacity 호출
          const storageResult = await getStorageCapacity(executeQuery);
          setStorageData(storageResult);
          data = storageResult.files || [];
        } else {
          // 일반 폴더, 휴지통 내 폴더, 공유 문서함 내 폴더, 또는 최근 문서함 내 폴더는 getFolderContents 호출
          // 내 드라이브(showTrash=false, showShared=false, showRecent=false, showBookmarks=false)인 경우 shared: false
          // 최근 문서함에서 폴더 진입 시 shared: true
          let shared = null;
          if (!showTrash && !showShared && !showRecent && !showBookmarks) {
            shared = false; // 내 드라이브
          } else if (showRecent) {
            shared = true; // 최근 문서함
          }
          data = await getFolderContents(executeQuery, currentFolder, shared);
        }
        setItems(data || []);
      } catch (error) {
        Message.showError({ message: '데이터를 불러오는 중 오류가 발생했습니다.' });
      }
    };
    
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFolder, showTrash, showShared, showRecent, showBookmarks, showStorage]);

  // Load bookmarks only once on mount
  // bookmarkId는 각 item 데이터에 포함되어 관리되므로 별도 useEffect 불필요

  // Load total usage on mount
  useEffect(() => {
    const loadTotalUsage = async () => {
      try {
        const usageData = await getTotalUsage(executeQuery);
        setTotalUsage(usageData);
      } catch (error) {
        console.error('전체 사용량 로드 중 오류:', error);
      }
    };
    
    loadTotalUsage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // 컴포넌트 마운트 시에만 실행

  // 애니메이션 효과를 위한 useEffect
  useEffect(() => {
    if (showStorage) {
      // 저장용량 메인 게이지 애니메이션 시작
      setStorageAnimationProgress(0);

      // 약간의 딜레이 후 애니메이션 시작 (데이터 로딩 완료 후)
      const startDelay = setTimeout(() => {
        const animationDuration = 200; // 0.8초
        const startTime = Date.now();

        const animateStorage = () => {
          const elapsed = Date.now() - startTime;
          const progress = Math.min(elapsed / animationDuration, 1);
          const easeOut = 1 - Math.pow(1 - progress, 3);

          setStorageAnimationProgress(easeOut);

          if (progress < 1) {
            requestAnimationFrame(animateStorage);
          }
        };

        requestAnimationFrame(animateStorage);
      }, 100); // 100ms 딜레이

      // cleanup 함수
      return () => {
        clearTimeout(startDelay);
      };
    } else {
      // 다른 화면으로 이동하면 애니메이션 리셋
      setStorageAnimationProgress(1);
    }
  }, [showStorage]);


  const formatFileSize = (bytes) => {
    if (!bytes) return '-';
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };


  const handleItemClick = (item, event = {}) => {
    // 다중 선택 처리
    if (event.ctrlKey || event.metaKey) {
      // Ctrl/Cmd 키가 눌린 경우 다중 선택
      if (selectedItems.includes(item.uuid)) {
        setSelectedItems(prev => prev.filter(id => id !== item.uuid));
        setSelectedItem(selectedItems.length <= 1 ? null : selectedItem);
      } else {
        setSelectedItems(prev => [...prev, item.uuid]);
        setSelectedItem(item);
      }
    } else if (event.shiftKey && selectedItems.length > 0) {
      // Shift 키가 눌린 경우 범위 선택
      // 정렬되고 필터된 아이템 목록 생성
      const sortedItems = sortItems(
        items.filter(i =>
          i.name.toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
      
      const currentIndex = sortedItems.findIndex(i => i.uuid === item.uuid);
      const lastSelectedIndex = sortedItems.findIndex(i => i.uuid === selectedItems[selectedItems.length - 1]);
      if (currentIndex !== -1 && lastSelectedIndex !== -1) {
        const start = Math.min(currentIndex, lastSelectedIndex);
        const end = Math.max(currentIndex, lastSelectedIndex);
        const rangeItems = sortedItems.slice(start, end + 1).map(i => i.uuid);
        setSelectedItems(rangeItems);
        setSelectedItem(item);
      }
    } else {
      // 일반 클릭 시 단일 선택
      if (selectedItems.includes(item.uuid) && selectedItems.length === 1) {
        // 이미 선택된 단일 아이템을 다시 클릭하면 선택 해제하지 않음
        return;
      } else {
        setSelectedItems([item.uuid]);
        setSelectedItem(item);
      }
    }
  };

  const handleItemDoubleClick = (item) => {
    // 더블클릭 시 폴더 이동 처리
    if (!item.extension) { // It's a folder
      setCurrentFolder(item.uuid);
      setBreadcrumbs(prev => [...prev, { name: item.name, uuid: item.uuid }]);
      setSelectedItems([]); // 이동 시 선택 해제
      setSelectedItem(null); // 선택된 아이템도 해제
    }
  };

  const handleBreadcrumbClick = (breadcrumb, index) => {
    setCurrentFolder(breadcrumb.uuid);
    setBreadcrumbs(prev => prev.slice(0, index + 1));
    setSelectedItems([]);
    setSelectedItem(null);
  };

  const handleContextMenu = (event, item) => {
    // openContextMenu에서 이미 preventDefault와 stopPropagation 처리됨
    openContextMenu(event, 'file', item);
  };


  const handleEmptySpaceContextMenu = (event) => {
    // 휴지통과 공유 문서함에서는 빈 공간 메뉴 비활성화
    if (showTrash || showShared) return;

    // openContextMenu에서 이미 preventDefault 처리됨
    openContextMenu(event, 'space');
  };

  const handleEmptySpaceClick = (event) => {
    // 우클릭은 무시 (컨텍스트 메뉴 간섭 방지)
    if (event.button === 2) return;
    
    // 빈 공간 클릭 시 선택 해제
    if (event.target === event.currentTarget) {
      setSelectedItems([]);
      setSelectedItem(null);
    }
  };





  const confirmRename = async () => {
    try {
      if (!renamingItem) {
        return;
      }

      if (renamingItem.extension) {
        await renameFile(executeMutation, renamingItem.uuid, renameValue);
      } else {
        await renameFolder(executeMutation, {
          uuid: renamingItem.uuid,
          folderName: renameValue
        });
      }
      loadCurrentFolderContents();
    } catch (error) {
      Message.showError({ message: '이름 변경 중 오류가 발생했습니다.' });
    }
    setRenameDialog(false);
    setRenameValue('');
    setRenamingItem(null);
  };

  const confirmNewFolder = async () => {
    try {
      const req = {
        upUuid: currentFolder || '', // 빈 문자열은 루트 폴더를 의미
        folderName: newFolderName
      };
      await generateFolder(executeMutation, req);
      loadCurrentFolderContents();
    } catch (error) {
      Message.showError({ message: '폴더 생성 중 오류가 발생했습니다.' });
    }
    setNewFolderDialog(false);
    setNewFolderName('');
  };

  const addUploadNotification = (fileName, status, error = null) => {
    const notification = {
      id: Date.now() + Math.random(),
      fileName,
      status, // 'uploading', 'success', 'error'
      error,
      timestamp: new Date()
    };

    setUploadNotifications(prev => [...prev, notification]);

    return notification.id;
  };


  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files);

    // 모든 파일에 대해 업로드 시작 알림 추가
    const notificationIds = files.map(file =>
      addUploadNotification(file.name, 'uploading')
    );

    try {
      // 일반 사용자 API 사용
      const results = await uploadFiles(currentFolder, files);

      // 각 파일의 결과에 따라 알림 업데이트
      results.forEach((result, index) => {
        const notificationId = notificationIds[index];

        setUploadNotifications(prev =>
          prev.map(n => n.id === notificationId
            ? {
                ...n,
                status: result.flagSuccess ? 'success' : 'error',
                error: result.flagSuccess ? null : '업로드 실패'
              }
            : n
          )
        );
      });

    } catch (error) {
      // 전체 업로드 실패 시 모든 파일을 실패로 처리
      notificationIds.forEach(notificationId => {
        setUploadNotifications(prev =>
          prev.map(n => n.id === notificationId
            ? { ...n, status: 'error', error: error.message }
            : n
          )
        );
      });
    }

    loadCurrentFolderContents();
    event.target.value = '';
  };







  const handleAdminUploadComplete = () => {
    loadCurrentFolderContents();
  };

  // 새로운 컨텍스트 메뉴 액션 핸들러
  const handleContextMenuAction = (action, selectedItem) => {
    switch (action) {
      case 'createFolder':
        setNewFolderName('새 폴더');
        setNewFolderDialog(true);
        break;
      case 'uploadFile':
        document.getElementById('file-upload').click();
        break;
      case 'uploadFolder':
        // 폴더 업로드 구현 (현재는 파일 업로드와 동일)
        document.getElementById('file-upload').click();
        break;
      case 'download':
        if (selectedItem && selectedItem.extension) {
          handleDownloadItem(selectedItem);
        }
        break;
      case 'rename':
        if (selectedItem && selectedItems.length <= 1) {
          setRenamingItem(selectedItem);
          setRenameValue(selectedItem.name);
          setRenameDialog(true);
        }
        break;
      case 'copy':
        if (selectedItem) {
          handleCopyItem(selectedItem);
        }
        break;
      case 'copySelected':
        handleCopySelectedItems();
        break;
      case 'move':
        if (selectedItem) {
          setMoveDialog({
            open: true,
            item: {
              uuid: selectedItem.uuid,
              name: selectedItem.name,
              isFolder: !selectedItem.extension,
              extension: selectedItem.extension,
              upUuid: selectedItem.upUuid
            }
          });
        }
        break;
      case 'addToFavorites':
        if (selectedItem) {
          handleToggleFavorite(selectedItem);
        }
        break;
      case 'addToFavoritesShared':
        if (selectedItem) {
          handleAddSharedToFavorites(selectedItem);
        }
        break;
      case 'delete':
        if (selectedItems.length > 1) {
          handleTrashSelected();
        } else if (selectedItem) {
          handleDeleteItem(selectedItem);
        }
        break;
      case 'properties':
        if (selectedItem && selectedItems.length <= 1) {
          // 우측 상세보기 패널 활성화 (단일 선택시만)
          setSelectedItem(selectedItem);
          setSelectedItems([selectedItem.uuid]);
        }
        break;
      case 'adminUpload':
        setAdminUploadDialog(true);
        break;
      case 'restore':
        if (selectedItems.length > 1) {
          handleRestoreSelected();
        } else if (selectedItem) {
          handleRestoreItem(selectedItem);
        }
        break;
      case 'permanentDelete':
        if (selectedItem) {
          setDeleteConfirmDialog({ open: true, item: selectedItem });
        }
        break;
      case 'share':
        if (selectedItem) {
          handleShareItem(selectedItem);
        }
        break;
      case 'downloadShared':
        if (selectedItem) {
          handleDownloadSharedItem(selectedItem);
        }
        break;
      case 'renameShared':
        if (selectedItem) {
          handleRenameSharedItem(selectedItem);
        }
        break;
      case 'copyToMyDrive':
        if (selectedItem) {
          handleCopyToMyDrive(selectedItem);
        }
        break;
      case 'deleteShared':
        if (selectedItem) {
          handleDeleteSharedItem(selectedItem);
        }
        break;
      default:
        // Unknown action
    }
  };

  const handleDownloadItem = async (item) => {
    try {
      const fileId = item.uuid;
      const response = await downloadFile(fileId);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = getDownloadFileName(response, getDisplayName(item));
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      Message.showError({ message: '다운로드 중 오류가 발생했습니다.' });
    }
  };

  const handleDeleteItem = async (item) => {
    try {
      if (item.extension) {
        await softDeleteFile(executeMutation, item.uuid);
      } else {
        await softDeleteFolder(executeMutation, item.uuid);
      }
      loadCurrentFolderContents();
    } catch (error) {
      Message.showError({ message: '삭제 중 오류가 발생했습니다.' });
    }
  };

  const handleRestoreItem = async (item) => {
    try {
      if (item.extension) {
        await restoreFile(executeMutation, item.uuid, item.upUuid);
      } else {
        await restoreFolder(executeMutation, item.uuid);
      }
      loadCurrentFolderContents();
    } catch (error) {
      Message.showError({ message: '복원 중 오류가 발생했습니다.' });
    }
  };

  // 다중 복원 함수
  const handleRestoreSelected = async () => {
    try {
      const fileUuids = [];
      const folderUuids = [];
      
      selectedItems.forEach(uuid => {
        const item = items.find(i => i.uuid === uuid);
        if (item) {
          if (item.extension) {
            fileUuids.push(uuid);
          } else {
            folderUuids.push(uuid);
          }
        }
      });

      await restoreSelected(executeMutation, fileUuids, folderUuids);
      // Message.showSuccess({ message: '선택된 항목들이 복원되었습니다.' });
      setSelectedItems([]);
      setSelectedItem(null);
      loadCurrentFolderContents();
    } catch (error) {
      Message.showError({ message: '복원 중 오류가 발생했습니다.' });
    }
  };

  // 다중 휴지통 이동 함수
  const handleTrashSelected = async () => {
    try {
      const fileUuids = [];
      const folderUuids = [];
      
      selectedItems.forEach(uuid => {
        const item = items.find(i => i.uuid === uuid);
        if (item) {
          if (item.extension) {
            fileUuids.push(uuid);
          } else {
            folderUuids.push(uuid);
          }
        }
      });

      await trashSelected(executeMutation, fileUuids, folderUuids);
      // Message.showSuccess({ message: '선택된 항목들이 휴지통으로 이동되었습니다.' });
      setSelectedItems([]);
      setSelectedItem(null);
      loadCurrentFolderContents();
    } catch (error) {
      Message.showError({ message: '삭제 중 오류가 발생했습니다.' });
    }
  };

  const handlePermanentDeleteItem = async (item) => {
    const notificationId = Date.now() + Math.random();
    const notificationItem = {
      id: notificationId,
      fileName: item.name,
      status: 'deleting',
      type: 'delete'
    };

    // 삭제 시작 알림 추가
    setUploadNotifications(prev => [...prev, notificationItem]);

    try {
      let result;
      if (item.extension) {
        // 파일인 경우
        result = await realDeleteFile(executeMutation, item.uuid);
        // 파일 삭제는 단순 boolean 반환이므로 성공으로 처리
        setUploadNotifications(prev => 
          prev.map(n => n.id === notificationId 
            ? { ...n, status: 'success' }
            : n
          )
        );
        
        // 3초 후 알림 자동 제거
        setTimeout(() => {
          setUploadNotifications(prev => prev.filter(n => n.id !== notificationId));
        }, 3000);
      } else {
        // 폴더인 경우
        result = await realDeleteSingleFolder(executeMutation, item.uuid);
        
        // HandleFilesResponse 배열 처리
        if (Array.isArray(result) && result.length > 0) {
          const allSuccess = result.every(r => r.flagSuccess);
          setUploadNotifications(prev => 
            prev.map(n => n.id === notificationId 
              ? { 
                  ...n, 
                  status: allSuccess ? 'success' : 'error',
                  details: result.map(r => ({
                    filename: r.originalFilename,
                    success: r.flagSuccess
                  }))
                }
              : n
            )
          );
          
          // 성공한 경우에만 3초 후 알림 자동 제거
          if (allSuccess) {
            setTimeout(() => {
              setUploadNotifications(prev => prev.filter(n => n.id !== notificationId));
            }, 3000);
          }
        } else {
          // 결과가 없는 경우 성공으로 처리
          setUploadNotifications(prev => 
            prev.map(n => n.id === notificationId 
              ? { ...n, status: 'success' }
              : n
            )
          );
          
          // 3초 후 알림 자동 제거
          setTimeout(() => {
            setUploadNotifications(prev => prev.filter(n => n.id !== notificationId));
          }, 3000);
        }
      }
      
      loadCurrentFolderContents();
    } catch (error) {
      // 에러 시 알림 상태 업데이트
      setUploadNotifications(prev => 
        prev.map(n => n.id === notificationId 
          ? { ...n, status: 'error', errorMessage: error.message }
          : n
        )
      );
      Message.showError({ message: '영구 삭제 중 오류가 발생했습니다.' });
    }
  };

  const handleToggleFavorite = async (item) => {
    try {
      // bookmarkId가 있으면 이미 즐겨찾기에 추가된 상태
      const isFavorite = !!item.bookmarkId;

      if (isFavorite) {
        if (item) {
          // bookmarkId가 있으면 bookmarkId 사용, 공유 문서함에서는 shareId 사용, 그 외에는 uuid 사용
          const deleteId = item.bookmarkId || (showShared ? item.shareId : item.uuid);
          await deleteDriveBookMark(executeMutation, deleteId);
          await loadBookmarks(); // 즐겨찾기 목록 새로고침

          // 현재 뷰의 데이터를 새로고침하여 즉시 UI 업데이트
          await loadCurrentFolderContents();
        } else {
          Message.showError({ message: '북마크 삭제에 실패했습니다.' });
        }
      } else {
        // 즐겨찾기에 추가
        const req = {
          uuid: null,
          shareId: null,
          extension: null
        };
        
        if (showShared && item.shareId) {
          // 공유 문서함 아이템
          req.shareId = item.shareId;
        } else {
          // 내 드라이브 아이템
          req.uuid = item.uuid;
          if (item.extension) {
            // 파일인 경우 extension도 포함
            req.extension = item.extension;
          }
        }
        
        await generateBookMark(executeMutation, req);
        await loadBookmarks(); // 즐겨찾기 목록 새로고침

        // 현재 뷰의 데이터를 새로고침하여 즉시 UI 업데이트
        await loadCurrentFolderContents();
      }
    } catch (error) {
      console.error('즐겨찾기 처리 중 오류:', error);
      Message.showError({ message: '즐겨찾기 처리 중 오류가 발생했습니다.' });
    }
  };

  const handleShareItem = async (item) => {
    try {
      const req = {
        uuid: item.uuid,
        extension: item.extension ? item.extension : null, // 파일인지 폴더인지 구분
        minReadLevel: 1, // 기본 읽기 권한 레벨
        minWriteLevel: 2, // 기본 쓰기 권한 레벨
        minCopyLevel: 1 // 기본 복사 권한 레벨
      };
      
      await createShare(executeMutation, req);
    } catch (error) {
      Message.showError({ 
        message: '공유 중 오류가 발생했습니다.' 
      });
      console.error('Share error:', error);
    }
  };

  // 공유 문서함에서 중요 문서함에 추가하는 함수
  const handleAddSharedToFavorites = async (item) => {
    try {
      const req = {
        uuid: null,
        shareId: item.shareId, // 공유 문서함 아이템은 shareId만 설정
        extension: null
      };
      
      await generateBookMark(executeMutation, req);
      await loadBookmarks(); // 즐겨찾기 목록 새로고침
    } catch (error) {
      console.error('중요 문서함 추가 중 오류:', error);
      Message.showError({ message: '중요 문서함 추가 중 오류가 발생했습니다.' });
    }
  };

  // 파일 사본 만들기 함수
  const handleCopyItem = async (item) => {
    try {
      await copyFile(executeMutation, item.uuid);
      
      // 현재 폴더 내용 새로고침
      // 내 드라이브에서만 shared: false, 최근 문서함에서는 shared: true, 나머지는 null
      let shared = null;
      if (!showTrash && !showShared && !showRecent && !showBookmarks) {
        shared = false; // 내 드라이브
      } else if (showRecent) {
        shared = true; // 최근 문서함
      }
      const data = await getFolderContents(executeQuery, currentFolder, shared);
      setItems(data || []);
      
      Message.showSuccess({ 
        message: `${getDisplayName(item)}의 사본이 만들어졌습니다.` 
      });
    } catch (error) {
      console.error('사본 만들기 중 오류:', error);
      Message.showError({ message: '사본 만들기 중 오류가 발생했습니다.' });
    }
  };

  // 복수 선택된 파일들의 사본 만들기 함수
  const handleCopySelectedItems = async () => {
    try {
      // 선택된 항목들 중 파일만 필터링 (폴더 제외)
      const selectedFiles = items.filter(item =>
        selectedItems.includes(item.uuid) && item.extension
      );

      if (selectedFiles.length === 0) {
        Message.showWarning({ message: '복사할 파일이 없습니다. 폴더는 복사할 수 없습니다.' });
        return;
      }

      const fileUuids = selectedFiles.map(file => file.uuid);

      // copySelected 호출 (Apollo Cache 오류 무시)
      try {
        await copySelected(executeMutation, fileUuids);
      } catch (apolloError) {
        // Apollo Cache 관련 오류는 무시하고 계속 진행
        console.warn('Apollo cache error (ignored):', apolloError);
      }

      // 현재 폴더 내용 새로고침
      let shared = null;
      if (!showTrash && !showShared && !showRecent && !showBookmarks) {
        shared = false; // 내 드라이브
      } else if (showRecent) {
        shared = true; // 최근 문서함
      }
      const data = await getFolderContents(executeQuery, currentFolder, shared);
      setItems(data || []);

      // 선택 해제
      setSelectedItems([]);

      Message.showSuccess({
        message: `${selectedFiles.length}개 파일의 사본이 만들어졌습니다.`
      });
    } catch (error) {
      console.error('복수 파일 사본 만들기 중 오류:', error);
      Message.showError({ message: '복수 파일 사본 만들기 중 오류가 발생했습니다.' });
    }
  };

  // 공유 문서함 전용 액션 핸들러들
  const handleDownloadSharedItem = async (item) => {
    try {
      // shareId 필드 사용
      const shareId = item.shareId;
      const response = await downloadShare(shareId);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = getDownloadFileName(response, getDisplayName(item));
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      Message.showError({ message: '공유 파일 다운로드 중 오류가 발생했습니다.' });
    }
  };

  const handleRenameSharedItem = async (item) => {
    try {
      const shareId = item.shareId;
      const driveType = item.extension ? 'FILE' : 'FOLDER';
      const newName = prompt('새 이름을 입력하세요:', item.name);
      
      if (newName && newName !== item.name) {
        await renameShare(executeMutation, shareId, driveType, newName);
        // Message.showSuccess({ 
        //   message: '이름이 변경되었습니다.' 
        // });
        loadCurrentFolderContents();
      }
    } catch (error) {
      Message.showError({ message: '이름 변경 중 오류가 발생했습니다.' });
    }
  };

  const handleCopyToMyDrive = async (item) => {
    try {
      const shareId = item.shareId;
      await copyShareFileToMyDrive(executeMutation, shareId);
      // Message.showSuccess({ 
      //   message: `${getDisplayName(item)}이(가) 내 드라이브로 복사되었습니다.` 
      // });
    } catch (error) {
      Message.showError({ message: '내 드라이브로 복사 중 오류가 발생했습니다.' });
    }
  };

  const handleDeleteSharedItem = async (item) => {
    setDeleteConfirmDialog({ open: true, item: item, isShared: true });
  };

  const handleConfirmDeleteShared = async (item) => {
    try {
      const shareId = item.shareId;
      const driveType = item.extension ? 'FILE' : 'FOLDER';
      
      await deleteShare(executeMutation, shareId, driveType);
      // Message.showSuccess({ 
      //   message: '공유가 삭제되었습니다.' 
      // });
      loadCurrentFolderContents();
    } catch (error) {
      Message.showError({ message: '공유 삭제 중 오류가 발생했습니다.' });
    }
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const sortItems = (items) => {
    return [...items].sort((a, b) => {
      let aValue = a[sortBy];
      let bValue = b[sortBy];

      // 폴더를 파일보다 먼저 표시
      if (!a.extension && b.extension) return -1;
      if (a.extension && !b.extension) return 1;

      // 값 처리
      if (sortBy === 'size') {
        aValue = aValue || 0;
        bValue = bValue || 0;
      } else if (sortBy === 'createDate' || sortBy === 'updateDate') {
        aValue = new Date(aValue || 0).getTime();
        bValue = new Date(bValue || 0).getTime();
      } else {
        aValue = String(aValue || '').toLowerCase();
        bValue = String(bValue || '').toLowerCase();
      }

      let comparison = 0;
      if (aValue < bValue) comparison = -1;
      if (aValue > bValue) comparison = 1;

      return sortOrder === 'desc' ? -comparison : comparison;
    });
  };

  // 휴지통 항목을 날짜별로 그룹화하는 함수
  const groupTrashItemsByDate = (items) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
    const lastWeek = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
    
    // 지난달 범위: 이전 달의 첫날부터 마지막날까지
    const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);

    const groups = {
      today: [],
      yesterday: [],
      lastWeek: [],
      lastMonth: [],
      older: []
    };

    items.forEach(item => {
      const itemDate = new Date(item.updateDate || item.createDate);
      const itemDateOnly = new Date(itemDate.getFullYear(), itemDate.getMonth(), itemDate.getDate());
      
      if (itemDateOnly.getTime() === today.getTime()) {
        groups.today.push(item);
      } else if (itemDateOnly.getTime() === yesterday.getTime()) {
        groups.yesterday.push(item);
      } else if (itemDateOnly >= lastWeek) {
        groups.lastWeek.push(item);
      } else if (itemDateOnly >= lastMonthStart && itemDateOnly <= lastMonthEnd) {
        // 지난주를 넘기고 지난달에 포함되는 경우
        groups.lastMonth.push(item);
      } else {
        groups.older.push(item);
      }
    });

    return groups;
  };


  const filteredAndSortedItems = sortItems(
    items.filter(item =>
      item.name.toLowerCase().includes(searchTerm.toLowerCase())
    )
  );


  // PathDisplay에서 폴더 클릭 시 호출되는 함수
  const handleNavigateToFolder = (uuid, folderName) => {
    if (showTrash || showShared) return; // 휴지통과 공유 문서함에서는 네비게이션 비활성화

    if (uuid === null) {
      // 루트로 이동
      setCurrentFolder(null);
      setBreadcrumbs([{ name: '내 드라이브', uuid: null }]);
    } else {
      // 특정 폴더로 이동 - 간단하게 uuid로만 이동
      setCurrentFolder(uuid);

      // breadcrumbs는 자동으로 업데이트되도록 하거나,
      // 여기서는 단순히 현재 폴더만 설정
      // 실제 breadcrumbs는 별도 로직이 필요할 수 있습니다.
    }

    setSelectedItems([]);
    setSelectedItem(null);
  };

  return (
    <Box sx={{ display: 'flex', height: '100vh', backgroundColor: '#f8f9fa' }}>
      {/* Left Sidebar */}
      <Drawer
        variant="permanent"
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
            position: 'relative',
            backgroundColor: '#f8f9fa',
            border: 'none',
          },
        }}
      >
        <Box sx={{
          position: 'relative',
          height: '120px'
        }}>
          <img
            src="/8Drive.png"
            alt="8Drive"
            style={{
              position: 'absolute',
              top: '-62px',
              left: '-10px',
              width: '200px',
              height: 'auto',
              objectFit: 'contain'
            }}
          />
        </Box>
        
        {/* Hidden file upload inputs */}
        <input
          accept="*/*"
          style={{ display: 'none' }}
          id="file-upload"
          multiple
          type="file"
          onChange={handleFileUpload}
        />

        <Divider sx={{ backgroundColor: 'transparent', my: 1 }} />

        <List onContextMenu={(e) => e.preventDefault()} sx={{ px: 1, py: 0 }}>
          {/* 홈 */}
          <ListItemButton
            selected={showHome}
            onClick={() => {
              setShowHome(true);
              setShowTrash(false);
              setShowShared(false);
              setShowRecent(false);
              setShowBookmarks(false);
              setShowStorage(false);
              setCurrentFolder(null);
              setBreadcrumbs([{ name: '홈', uuid: null }]);
              setSelectedItems([]);
              setSelectedItem(null);
            }}
            onContextMenu={(e) => e.preventDefault()}
            sx={{
              borderRadius: '0 20px 20px 0',
              mr: 1,
              mb: 2,
              minHeight: '40px',
              '&:hover': {
                backgroundColor: '#f1f3f4'
              },
              '&.Mui-selected': {
                backgroundColor: '#e8f0fe',
                '&:hover': {
                  backgroundColor: '#e8f0fe'
                },
                '& .MuiListItemIcon-root, & .MuiListItemText-root': {
                  color: '#1a73e8'
                }
              }
            }}
          >
            <ListItemIcon sx={{ minWidth: '32px' }}>
              <HomeIcon sx={{ fontSize: '20px' }} />
            </ListItemIcon>
            <ListItemText
              primary="홈"
              sx={{ '& .MuiTypography-root': { fontSize: '14px', fontWeight: 500 } }}
            />
          </ListItemButton>

          {/* 내 드라이브 */}
          <ListItemButton
            selected={!showTrash && !showShared && !showRecent && !showBookmarks && !showStorage && !showHome}
            onClick={() => {
              setShowHome(false);
              setShowTrash(false);
              setShowShared(false);
              setShowRecent(false);
              setShowBookmarks(false);
              setShowStorage(false);
              setCurrentFolder(null);
              setBreadcrumbs([{ name: '내 드라이브', uuid: null }]);
              setSelectedItems([]);
              setSelectedItem(null);
            }}
            onContextMenu={(e) => e.preventDefault()}
            sx={{ 
              borderRadius: '0 20px 20px 0',
              mr: 1,
              mb: 2,
              minHeight: '40px',
              '&:hover': {
                backgroundColor: '#f1f3f4'
              },
              '&.Mui-selected': {
                backgroundColor: '#e8f0fe',
                '&:hover': {
                  backgroundColor: '#e8f0fe'
                },
                '& .MuiListItemIcon-root, & .MuiListItemText-root': {
                  color: '#1a73e8'
                }
              }
            }}
          >
            <ListItemIcon sx={{ minWidth: '32px' }}>
              <MyDriveIcon sx={{ fontSize: '20px' }} />
            </ListItemIcon>
            <ListItemText
              primary="내 드라이브"
              sx={{ '& .MuiTypography-root': { fontSize: '14px', fontWeight: 500 } }}
            />
          </ListItemButton>

          {/* 공유 문서함 */}
          <ListItemButton
            selected={showShared}
            onClick={() => {
              setShowHome(false);
              setShowShared(true);
              setShowTrash(false);
              setShowRecent(false);
              setShowBookmarks(false);
              setShowStorage(false);
              setCurrentFolder(null);
              setBreadcrumbs([{ name: '공유 문서함', uuid: null }]);
              setSelectedItems([]);
              setSelectedItem(null);
            }}
            onContextMenu={(e) => e.preventDefault()}
            sx={{ 
              borderRadius: '0 20px 20px 0',
              mr: 1,
              mb: 0.5,
              minHeight: '40px',
              '&:hover': {
                backgroundColor: '#f1f3f4'
              },
              '&.Mui-selected': {
                backgroundColor: '#e8f0fe',
                '&:hover': {
                  backgroundColor: '#e8f0fe'
                },
                '& .MuiListItemIcon-root, & .MuiListItemText-root': {
                  color: '#1a73e8'
                }
              }
            }}
          >
            <ListItemIcon sx={{ minWidth: '32px' }}>
              <PeopleIcon sx={{ fontSize: '20px' }} />
            </ListItemIcon>
            <ListItemText
              primary="공유 문서함"
              sx={{ '& .MuiTypography-root': { fontSize: '14px', fontWeight: 500 } }}
            />
          </ListItemButton>

          {/* 최근 문서함 */}
          <ListItemButton
            selected={showRecent && !showTrash && !showShared}
            onClick={() => {
              setShowHome(false);
              setShowRecent(true);
              setShowTrash(false);
              setShowShared(false);
              setShowBookmarks(false);
              setShowStorage(false);
              setCurrentFolder(null);
              setBreadcrumbs([{ name: '최근 문서함', uuid: null }]);
              setSelectedItems([]);
              setSelectedItem(null);
            }}
            sx={{ 
              borderRadius: '0 20px 20px 0',
              mr: 1,
              mb: 0.5,
              minHeight: '40px',
              '&:hover': {
                backgroundColor: '#f1f3f4'
              },
              '&.Mui-selected': {
                backgroundColor: '#e8f0fe',
                '&:hover': {
                  backgroundColor: '#e8f0fe'
                },
                '& .MuiListItemIcon-root, & .MuiListItemText-root': {
                  color: '#1a73e8'
                }
              }
            }}
          >
            <ListItemIcon sx={{ minWidth: '32px' }}>
              <AccessTimeIcon sx={{ fontSize: '20px' }} />
            </ListItemIcon>
            <ListItemText
              primary="최근 문서함"
              sx={{ '& .MuiTypography-root': { fontSize: '14px', fontWeight: 500 } }}
            />
          </ListItemButton>

          {/* 중요 문서함 */}
          <ListItemButton
            selected={showBookmarks}
            onClick={() => {
              setShowHome(false);
              setShowBookmarks(true);
              setShowRecent(false);
              setShowTrash(false);
              setShowShared(false);
              setShowStorage(false);
              setCurrentFolder(null);
              setBreadcrumbs([{ name: '중요 문서함', uuid: null }]);
              setSelectedItems([]);
              setSelectedItem(null);
              // 중요 문서함은 getDriveBookMark를 사용하여 북마크 아이템들을 로드
              const loadBookmarkData = async () => {
                try {
                  const data = await getDriveBookMark(executeQuery);
                  setItems(data || []);
                } catch (error) {
                  Message.showError({ message: '중요 문서함을 불러오는 중 오류가 발생했습니다.' });
                }
              };
              loadBookmarkData();
            }}
            sx={{ 
              borderRadius: '0 20px 20px 0',
              mr: 1,
              mb: 2,
              minHeight: '40px',
              '&:hover': {
                backgroundColor: '#f1f3f4'
              },
              '&.Mui-selected': {
                backgroundColor: '#e8f0fe',
                '&:hover': {
                  backgroundColor: '#e8f0fe'
                },
                '& .MuiListItemIcon-root, & .MuiListItemText-root': {
                  color: '#1a73e8'
                }
              }
            }}
          >
            <ListItemIcon sx={{ minWidth: '32px' }}>
              <StarBorderIcon sx={{ fontSize: '20px' }} />
            </ListItemIcon>
            <ListItemText
              primary="중요 문서함"
              sx={{ '& .MuiTypography-root': { fontSize: '14px', fontWeight: 500 } }}
            />
          </ListItemButton>

          {/* 휴지통 */}
          <ListItemButton
            selected={showTrash}
            onClick={() => {
              setShowHome(false);
              setShowTrash(true);
              setShowShared(false);
              setShowRecent(false);
              setShowBookmarks(false);
              setShowStorage(false);
              setCurrentFolder(null);
              setBreadcrumbs([{ name: '휴지통', uuid: null }]);
              setSelectedItems([]);
              setSelectedItem(null);
            }}
            onContextMenu={(e) => e.preventDefault()}
            sx={{ 
              borderRadius: '0 20px 20px 0',
              mr: 1,
              mb: 0.5,
              minHeight: '40px',
              '&:hover': {
                backgroundColor: '#f1f3f4'
              },
              '&.Mui-selected': {
                backgroundColor: '#e8f0fe',
                '&:hover': {
                  backgroundColor: '#e8f0fe'
                },
                '& .MuiListItemIcon-root, & .MuiListItemText-root': {
                  color: '#1a73e8'
                }
              }
            }}
          >
            <ListItemIcon sx={{ minWidth: '32px' }}>
              <DeleteIcon sx={{ fontSize: '20px' }} />
            </ListItemIcon>
            <ListItemText
              primary="휴지통"
              sx={{ '& .MuiTypography-root': { fontSize: '14px', fontWeight: 500 } }}
            />
          </ListItemButton>

          {/* 저장용량 */}
          <ListItemButton
            selected={showStorage}
            onClick={() => {
              setShowHome(false);
              setShowStorage(true);
              setShowTrash(false);
              setShowShared(false);
              setShowRecent(false);
              setShowBookmarks(false);
              setCurrentFolder(null);
              setBreadcrumbs([{ name: '저장용량', uuid: null }]);
              setSelectedItems([]);
              setSelectedItem(null);
              // 저장용량 데이터 로드
              const loadStorageData = async () => {
                try {
                  const data = await getStorageCapacity(executeQuery);
                  setStorageData(data);
                  setItems(data.files || []);
                } catch (error) {
                  Message.showError({ message: '저장용량 정보를 불러오는 중 오류가 발생했습니다.' });
                }
              };
              loadStorageData();
            }}
            sx={{ 
              borderRadius: '0 20px 20px 0',
              mr: 1,
              minHeight: '40px',
              flexDirection: 'column',
              alignItems: 'flex-start',
              py: 1.5,
              '&:hover': {
                backgroundColor: '#f1f3f4'
              },
              '&.Mui-selected': {
                backgroundColor: '#e8f0fe',
                '&:hover': {
                  backgroundColor: '#e8f0fe'
                },
                '& .MuiListItemIcon-root, & .MuiListItemText-root': {
                  color: '#1a73e8'
                }
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', mb: 0.5 }}>
              <ListItemIcon sx={{ minWidth: '32px' }}>
                <StorageIcon sx={{ fontSize: '20px', color: showStorage ? '#1a73e8' : '#5f6368' }} />
              </ListItemIcon>
              <ListItemText
                primary="저장용량"
                sx={{ '& .MuiTypography-root': { fontSize: '14px', fontWeight: 500, color: showStorage ? '#1a73e8' : '#202124' } }}
              />
            </Box>
            <Box sx={{ width: '100%', pl: 4 }}>
              <LinearProgress
                variant="determinate"
                value={(() => {
                  // totalUsage 데이터를 우선 사용 (maxUsage: GB, used: bytes)
                  if (totalUsage.maxUsage > 0) {
                    const maxBytes = totalUsage.maxUsage * 1024 * 1024 * 1024; // GB를 바이트로 변환
                    return Math.min((totalUsage.used / maxBytes) * 100, 100);
                  }
                  // storageData가 있으면 사용
                  if (storageData?.meta?.length > 0 && storageData.maxStorageGb > 0) {
                    const totalUsed = storageData.meta.reduce((sum, user) => sum + (user.usage || 0), 0);
                    const maxBytes = storageData.maxStorageGb * 1024 * 1024 * 1024;
                    return Math.min((totalUsed / maxBytes) * 100, 100);
                  }
                  // 기본값
                  return 15;
                })()}
                sx={{ 
                  height: 3,
                  borderRadius: 2,
                  backgroundColor: '#f1f3f4',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: '#1a73e8'
                  }
                }} 
              />
              <Typography variant="caption" sx={{ color: '#5f6368', fontSize: '12px', mt: 0.5, display: 'block' }}>
                {(() => {
                  // totalUsage 데이터를 우선 사용 (이미 GB 단위)
                  if (totalUsage.maxUsage > 0) {
                    const maxGB = totalUsage.maxUsage.toFixed(0);
                    const usedGB = (totalUsage.used / (1024 * 1024 * 1024)).toFixed(1); // used는 바이트 단위
                    return `${maxGB}GB 중 ${usedGB}GB 사용됨`;
                  }
                  // storageData가 있으면 사용
                  if (storageData?.meta?.length > 0 && storageData.maxStorageGb > 0) {
                    const totalUsed = storageData.meta.reduce((sum, user) => sum + (user.usage || 0), 0);
                    const usedGB = (totalUsed / (1024 * 1024 * 1024)).toFixed(1);
                    return `${storageData.maxStorageGb}GB 중 ${usedGB}GB 사용됨`;
                  }
                  // 데이터가 없는 경우
                  return '저장용량 정보 없음';
                })()}
              </Typography>
            </Box>
          </ListItemButton>
        </List>
      </Drawer>

      {/* Main Content Area */}
      <Box component="main" sx={{ 
        flexGrow: 1, 
        display: 'flex', 
        flexDirection: 'column', 
        backgroundColor: '#f8f9fa', 
        p: 0.5, 
        pb: 7,
        maxWidth: selectedItem ? 'calc(100% - 250px)' : '100%',
        transition: 'max-width 0.3s ease'
      }}>
        {/* Top Toolbar */}
        <AppBar position="static" color="transparent" elevation={0} sx={{ backgroundColor: 'transparent', border: 'none', mb: 1 }}>
          <Toolbar sx={{ minHeight: '64px !important', px: 2 }}>
            {/* 좌측: Breadcrumbs */}
            <Breadcrumbs 
              separator={<ChevronRightIcon fontSize="small" sx={{ color: '#5f6368' }} />} 
              sx={{ 
                flexGrow: 1,
                overflow: 'hidden',
                '& .MuiBreadcrumbs-ol': {
                  alignItems: 'center',
                  flexWrap: 'nowrap'
                }
              }}
            >
              {breadcrumbs.map((breadcrumb, index) => {
                const truncateText = (text, maxLength = 10) => {
                  if (text.length <= maxLength) return text;
                  return text.substring(0, maxLength) + '..';
                };
                
                return (
                  <Link
                    key={breadcrumb.uuid || 'root'}
                    color="inherit"
                    component="button"
                    onClick={() => handleBreadcrumbClick(breadcrumb, index)}
                    title={breadcrumb.name}
                    sx={{
                      textDecoration: 'none',
                      color: '#202124',
                      fontSize: '18px',
                      fontWeight: 500,
                      display: 'flex',
                      alignItems: 'center',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      maxWidth: selectedItem ? '120px' : '180px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      '&:hover': {
                        color: '#1a73e8',
                        backgroundColor: '#f1f3f4'
                      }
                    }}
                  >
                    {index === 0 && (
                      <>
                        {showHome && <HomeIcon sx={{ mr: 0.5, fontSize: 20, color: '#5f6368', flexShrink: 0 }} />}
                        {!showHome && !showShared && !showRecent && !showBookmarks && !showStorage && !showTrash && <MyDriveIcon sx={{ mr: 0.5, fontSize: 20, color: '#5f6368', flexShrink: 0 }} />}
                        {showShared && <PeopleIcon sx={{ mr: 0.5, fontSize: 20, color: '#5f6368', flexShrink: 0 }} />}
                        {showRecent && <AccessTimeIcon sx={{ mr: 0.5, fontSize: 20, color: '#5f6368', flexShrink: 0 }} />}
                        {showBookmarks && <StarBorderIcon sx={{ mr: 0.5, fontSize: 20, color: '#5f6368', flexShrink: 0 }} />}
                        {showStorage && <StorageIcon sx={{ mr: 0.5, fontSize: 20, color: '#5f6368', flexShrink: 0 }} />}
                        {showTrash && <DeleteIcon sx={{ mr: 0.5, fontSize: 20, color: '#5f6368', flexShrink: 0 }} />}
                      </>
                    )}
                    <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {selectedItem ? truncateText(breadcrumb.name, 8) : truncateText(breadcrumb.name, 12)}
                    </Box>
                  </Link>
                );
              })}
            </Breadcrumbs>

            {/* 우측: 검색 + 보기 모드 버튼들 (항상 우측 끝에 고정) */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField
                size="small"
                placeholder="드라이브 검색"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#5f6368' }} />
                    </InputAdornment>
                  )
                }}
                sx={{
                  width: 280,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#f8f9fa',
                    borderRadius: '8px',
                    '& fieldset': {
                      borderColor: '#dadce0',
                    },
                    '&:hover fieldset': {
                      borderColor: '#dadce0',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#1a73e8',
                    },
                    '&.Mui-focused': {
                      backgroundColor: '#ffffff'
                    }
                  },
                  '& .MuiOutlinedInput-input': {
                    color: '#202124',
                    fontSize: '14px',
                    fontWeight: '400',
                    '&::placeholder': {
                      color: '#5f6368',
                      opacity: 1
                    }
                  }
                }}
              />
              
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <IconButton
                  onClick={() => setView('list')}
                  title="목록 보기"
                  sx={{
                    backgroundColor: view === 'list' ? '#e8f0fe' : 'transparent',
                    color: view === 'list' ? '#1a73e8' : '#5f6368',
                    '&:hover': {
                      backgroundColor: view === 'list' ? '#e8f0fe' : '#f1f3f4'
                    }
                  }}
                >
                  <ViewListIcon />
                </IconButton>
                <IconButton
                  onClick={() => setView('grid')}
                  title="격자 보기"
                  sx={{
                    backgroundColor: view === 'grid' ? '#e8f0fe' : 'transparent',
                    color: view === 'grid' ? '#1a73e8' : '#5f6368',
                    '&:hover': {
                      backgroundColor: view === 'grid' ? '#e8f0fe' : '#f1f3f4'
                    }
                  }}
                >
                  <ViewModuleIcon />
                </IconButton>
              </Box>
            </Box>
          </Toolbar>
        </AppBar>

        {/* Content Area */}
        <Box
          sx={{
            flexGrow: 1,
            p: 2,
            overflow: 'auto',
            backgroundColor: '#ffffff',
            borderRadius: '12px',
            maxHeight: 'calc(100vh - 190px)',
            boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)',
            position: 'relative'
          }}
          onContextMenu={handleEmptySpaceContextMenu}
          onMouseDown={handleEmptySpaceClick}
        >
        
          {showStorage ? (
            // Google Drive style storage capacity view
            <Box sx={{ p: 2 }}>
              {/* Storage usage overview */}
              <Box sx={{ 
                p: 3,
                mb: 3,
              }}>
                <Typography variant="h6" sx={{ mb: 3, color: '#202124' }}>
                  {storageData.maxStorageGb}GB 중 {(() => {
                    if (!storageData.meta || storageData.meta.length === 0) {
                      return '0.0';
                    }
                    const totalUsed = storageData.meta.reduce((sum, user) => sum + (user.usage || 0), 0);
                    return (totalUsed / (1024 * 1024 * 1024)).toFixed(1);
                  })()}GB 사용됨
                </Typography>
                
                {/* Enhanced multi-color storage gauge with internal breakdown */}
                <Box sx={{ position: 'relative', mb: 2 }}>
                  {/* Main storage gauge */}
                  <Box sx={{ 
                    position: 'relative',
                    height: 16,
                    borderRadius: 8,
                    backgroundColor: '#e8eaed',
                    overflow: 'hidden',
                    cursor: 'pointer',
                    border: '1px solid #dadce0',
                    mb: 3,
                    display: 'flex'
                  }}>
                    {(() => {
                      const colors = ['#1a73e8', '#34a853', '#fbbc04', '#ea4335', '#9c27b0', '#ff9800'];
                      const maxBytes = storageData.maxStorageGb * 1024 * 1024 * 1024;

                      // 데이터가 없는 경우 빈 게이지 표시
                      if (!storageData.meta || storageData.meta.length === 0) {
                        return null;
                      }

                      // 사용량으로 내림차순 정렬 (높은 사용량에서 낮은 사용량 순)
                      const sortedMeta = [...storageData.meta].sort((a, b) => (b.usage || 0) - (a.usage || 0));

                      return sortedMeta.map((user, index) => {
                        const userUsage = user.usage || 0;
                        let userPercentage = maxBytes > 0 ? (userUsage / maxBytes) * 100 : 0;

                        // 최소 폭 보장 (0.5% 이상)
                        if (userPercentage > 0 && userPercentage < 0.5) {
                          userPercentage = 0.5;
                        }

                        // 사용량이 없는 사용자는 표시하지 않음
                        if (userPercentage === 0) {
                          return null;
                        }

                        return (
                          <Tooltip
                            key={user.clientId || index}
                            title={`${user.clientName || `사용자 ${index + 1}`}: ${((user.usage || 0) / (1024 * 1024 * 1024)).toFixed(2)}GB (${userPercentage.toFixed(1)}%)`}
                            arrow
                          >
                            <Box
                              sx={{
                                flex: `0 0 ${userPercentage * storageAnimationProgress}%`,
                                height: '100%',
                                backgroundColor: colors[index % colors.length],
                                transition: 'all 0.3s ease',
                                borderRight: index < sortedMeta.length - 1 ? '1px solid rgba(255,255,255,0.8)' : 'none',
                                '&:hover': {
                                  opacity: 0.8,
                                  transform: 'scaleY(1.1)'
                                }
                              }}
                            />
                          </Tooltip>
                        );
                      }).filter(Boolean);
                    })()}
                  </Box>

                  {/* Simple user legend */}
                  <Box sx={{ 
                    mt: 2,
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 2,
                    alignItems: 'center'
                  }}>
                    {(() => {
                      const colors = ['#1a73e8', '#34a853', '#fbbc04', '#ea4335', '#9c27b0', '#ff9800'];

                      // 데이터가 없는 경우 범례도 표시하지 않음
                      if (!storageData.meta || storageData.meta.length === 0) {
                        return (
                          <Typography variant="body2" sx={{ color: '#5f6368', fontStyle: 'italic' }}>
                            사용자별 사용량 데이터가 없습니다.
                          </Typography>
                        );
                      }

                      const sortedMeta = [...storageData.meta].sort((a, b) => (b.usage || 0) - (a.usage || 0));

                      return sortedMeta.map((user, index) => (
                        <Box key={user.clientId || index} sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1
                        }}>
                          <Box sx={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            backgroundColor: colors[index % colors.length],
                            flexShrink: 0
                          }} />
                          <Typography variant="body2" sx={{
                            fontSize: '13px',
                            color: '#202124'
                          }}>
                            {user.clientName || user.clientId || '알 수 없음'}
                          </Typography>
                        </Box>
                      ));
                    })()}
                  </Box>
                </Box>
              </Box>

              {/* File list with storage info */}
              <Typography variant="h6" sx={{ mb: 2, color: '#202124' }}>
                파일 목록 ({storageData.files?.length || 0}개 파일)
              </Typography>
              
              <FileListView
                view={view}
                items={storageData.files ? sortItems(
                  storageData.files.filter(item =>
                    item.name.toLowerCase().includes(searchTerm.toLowerCase())
                  )
                ) : []}
                viewType="storage"
                selectedItems={selectedItems}
                selectedItem={selectedItem}
                sortBy={sortBy}
                sortOrder={sortOrder}
                loading={loading}
                onSort={handleSort}
                onItemClick={handleItemClick}
                onItemDoubleClick={handleItemDoubleClick}
                onContextMenu={handleContextMenu}
                onToggleFavorite={handleToggleFavorite}
                getDisplayName={getDisplayName}
                formatFileSize={formatFileSize}
              />
            </Box>
          ) : showHome ? (
            <DriveDashboard />
          ) : (
            <FileListView
              view={view}
              items={filteredAndSortedItems}
              viewType={showTrash ? 'trash' : showShared ? 'shared' : showRecent ? 'recent' : showBookmarks ? 'bookmarks' : 'myDrive'}
              selectedItems={selectedItems}
              selectedItem={selectedItem}
              sortBy={sortBy}
              sortOrder={sortOrder}
              loading={loading}
              onSort={handleSort}
              onItemClick={handleItemClick}
              onItemDoubleClick={handleItemDoubleClick}
              onContextMenu={handleContextMenu}
              onToggleFavorite={handleToggleFavorite}
              onRename={(item) => {
                if (selectedItems.length <= 1) {
                  setRenamingItem(item);
                  setRenameValue(item.name);
                  setRenameDialog(true);
                }
              }}
              onDownload={async (item) => {
                try {
                  const fileId = item.uuid;
                  const response = await downloadFile(fileId);
                  
                  // 백엔드 Content-Disposition 헤더에서 파일명 추출 시도
                  let fileName = getDisplayName(item);
                  const contentDisposition = response.headers.get('Content-Disposition');
                  if (contentDisposition) {
                    // filename*=UTF-8''encoded_filename 형태 파싱
                    const utf8Match = contentDisposition.match(/filename\*=UTF-8''(.+)/);
                    if (utf8Match) {
                      fileName = decodeURIComponent(utf8Match[1]);
                    } else {
                      // filename="filename" 형태 파싱
                      const normalMatch = contentDisposition.match(/filename="(.+)"/);
                      if (normalMatch) {
                        fileName = normalMatch[1];
                      }
                    }
                  }
                  
                  const blob = await response.blob();
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.style.display = 'none';
                  a.href = url;
                  a.download = fileName;
                  document.body.appendChild(a);
                  a.click();
                  window.URL.revokeObjectURL(url);
                  document.body.removeChild(a);
                } catch (error) {
                  console.error('Download error:', error);
                  Message.showError({ message: '다운로드 중 오류가 발생했습니다.' });
                }
              }}
              onEmptyTrash={async () => {
                try {
                  // 파일과 폴더를 분리
                  const fileUuids = items.filter(item => item.extension).map(item => item.uuid);
                  const folderUuids = items.filter(item => !item.extension).map(item => item.uuid);
                  
                  // 새로운 emptyTrashProcess API 호출
                  const result = await emptyTrashProcess(executeMutation, fileUuids, folderUuids);
                  
                  // 성공/실패 결과 처리
                  const successCount = result.filter(r => r.flagSuccess).length;
                  const failCount = result.filter(r => !r.flagSuccess).length;
                  
                  if (failCount === 0) {
                    // Message.showSuccess({ message: '휴지통이 비워졌습니다.' });
                  } else if (successCount > 0) {
                    Message.showWarning({ message: `${successCount}개 항목이 삭제되었으나 ${failCount}개 항목 삭제에 실패했습니다.` });
                  } else {
                    Message.showError({ message: '휴지통 비우기에 실패했습니다.' });
                  }
                  
                  // 휴지통 새로고침
                  if (successCount > 0) {
                    await loadCurrentFolderContents();
                  }
                } catch (error) {
                  console.error('Empty trash error:', error);
                  Message.showError({ message: '휴지통 비우기 중 오류가 발생했습니다.' });
                }
              }}
              getDisplayName={getDisplayName}
              formatFileSize={formatFileSize}
              groupTrashItemsByDate={groupTrashItemsByDate}
            />
          )}

        </Box>
      </Box>

      {/* Rename Dialog */}
      <Dialog open={renameDialog} onClose={() => setRenameDialog(false)}>
        <DialogTitle>이름 바꾸기</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            margin="dense"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameDialog(false)}>취소</Button>
          <Button onClick={confirmRename} variant="contained">확인</Button>
        </DialogActions>
      </Dialog>

      {/* New Folder Dialog */}
      <Dialog
        open={newFolderDialog}
        onClose={() => {
          setNewFolderDialog(false);
          setNewFolderName('');
        }}
        maxWidth="xs"
        PaperProps={{
          sx: {
            width: '320px',
            minHeight: '180px'
          }
        }}
      >
        <DialogTitle sx={{ fontSize: '18px', fontWeight: 500, pb: 1 }}>
          새 폴더 만들기
        </DialogTitle>
        <DialogContent sx={{ pt: 2, pb: 3 }}>
          <TextField
            autoFocus
            fullWidth
            placeholder="폴더 이름을 입력하세요"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            margin="dense"
            size="medium"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                fontSize: '14px'
              }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={() => {
              setNewFolderDialog(false);
              setNewFolderName('');
            }}
            color="inherit"
          >
            취소
          </Button>
          <Button 
            onClick={confirmNewFolder}
            variant="contained"
            disabled={!newFolderName.trim()}
          >
            만들기
          </Button>
        </DialogActions>
      </Dialog>


      {/* Upload Notification Popup */}
      <UploadNotificationPopup
        notifications={uploadNotifications}
        onClose={() => setUploadNotifications([])}
        onClearAll={() => setUploadNotifications([])}
      />

      {/* Path Display - Google Drive style bottom bar */}
      <PathDisplay 
        selectedItem={selectedItem} 
        showTrash={showTrash}
        showShared={showShared}
        onNavigateToFolder={handleNavigateToFolder}
        currentBreadcrumbs={breadcrumbs}
      />

      {/* File Details Panel - Right Sidebar */}
      {selectedItem && (
        <FileDetailsPanel
          selectedItem={selectedItem}
          onClose={() => {
            setSelectedItem(null);
            setSelectedItems([]);
          }}
        />
      )}

      {/* Admin Upload Dialog */}
      <AdminUploadDialog
        open={adminUploadDialog}
        onClose={() => setAdminUploadDialog(false)}
        currentFolder={currentFolder}
        onUploadComplete={handleAdminUploadComplete}
      />

      {/* Move Dialog */}
      <MoveDialog
        open={moveDialog.open}
        onClose={() => setMoveDialog({ open: false, item: null })}
        itemToMove={moveDialog.item}
        onMoveComplete={() => {
          setMoveDialog({ open: false, item: null });
          loadCurrentFolderContents();
        }}
        executeQuery={executeQuery}
        executeMutation={executeMutation}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmDialog.open}
        onClose={() => setDeleteConfirmDialog({ open: false, item: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          color: '#ea4335',
          fontWeight: 600
        }}>
          <DeleteForeverIcon sx={{ color: '#ea4335' }} />
          {deleteConfirmDialog.isShared ? '공유 삭제 확인' : '영구 삭제 확인'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ py: 1 }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              <strong>"{deleteConfirmDialog.item?.name}"</strong>을(를) {deleteConfirmDialog.isShared ? '공유에서 삭제' : '영구적으로 삭제'}하시겠습니까?
            </Typography>
            <Box sx={{ 
              backgroundColor: '#fef7e0', 
              border: '1px solid #fbbc04', 
              borderRadius: 2, 
              p: 2,
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1
            }}>
              <PriorityHighIcon sx={{ color: '#f57c00', fontSize: 20, mt: 0.1 }} />
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#e65100', mb: 0.5 }}>
                  {deleteConfirmDialog.isShared ? '주의: 공유가 삭제됩니다' : '주의: 이 작업은 되돌릴 수 없습니다'}
                </Typography>
                <Typography variant="body2" sx={{ color: '#bf360c' }}>
                  {deleteConfirmDialog.isShared 
                    ? '해당 항목의 공유가 삭제되며, 다른 사용자들이 더 이상 접근할 수 없습니다.' 
                    : '삭제된 항목은 복구할 수 없으며, 모든 데이터가 완전히 제거됩니다.'
                  }
                </Typography>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={() => setDeleteConfirmDialog({ open: false, item: null })}
            color="inherit"
          >
            취소
          </Button>
          <Button 
            onClick={() => {
              if (deleteConfirmDialog.item) {
                if (deleteConfirmDialog.isShared) {
                  handleConfirmDeleteShared(deleteConfirmDialog.item);
                } else {
                  handlePermanentDeleteItem(deleteConfirmDialog.item);
                }
                setDeleteConfirmDialog({ open: false, item: null });
              }
            }}
            variant="contained"
            color="error"
            startIcon={<DeleteForeverIcon />}
          >
            {deleteConfirmDialog.isShared ? '공유 삭제' : '영구 삭제'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 새로운 컨텍스트 메뉴 */}
      <ContextMenu
        isOpen={contextMenu.isOpen}
        position={contextMenu.position}
        menuType={contextMenu.menuType}
        selectedItem={contextMenu.selectedItem}
        selectedItems={selectedItems}
        onClose={closeContextMenu}
        onItemClick={handleContextMenuAction}
        showTrash={showTrash}
        viewType={showTrash ? 'trash' : showShared ? 'shared' : 'myDrive'}
      />
    </Box>
  );
};

export default DriveManagement;
